import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { RichTextEditor } from '../../components/common/RichTextEditor';
import { blogService } from '../../services/firebase/blog';
import { BlogPost, BlogPostInput, BlogPostStatus } from '../../types/blog';

interface BlogFormState {
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  status: BlogPostStatus;
  publishAt: string;
  tags: string;
  coverImageUrl: string;
  seoTitle: string;
  seoDescription: string;
  ogImageUrl: string;
  canonicalUrl: string;
}

const emptyState: BlogFormState = {
  title: '',
  slug: '',
  excerpt: '',
  content: '',
  status: 'draft',
  publishAt: '',
  tags: '',
  coverImageUrl: '',
  seoTitle: '',
  seoDescription: '',
  ogImageUrl: '',
  canonicalUrl: '',
};

const slugify = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

const formatDateTime = (date?: Date | null) => {
  if (!date) {
    return 'Not set';
  }

  return new Intl.DateTimeFormat('en-GB', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const mapPostToState = (post: BlogPost): BlogFormState => ({
  title: post.title,
  slug: post.slug,
  excerpt: post.excerpt,
  content: post.content,
  status: post.status,
  publishAt: post.publishAt ? new Date(post.publishAt.getTime() - post.publishAt.getTimezoneOffset() * 60000).toISOString().slice(0, 16) : '',
  tags: post.tags.join(', '),
  coverImageUrl: post.coverImageUrl || '',
  seoTitle: post.seoTitle || '',
  seoDescription: post.seoDescription || '',
  ogImageUrl: post.ogImageUrl || '',
  canonicalUrl: post.canonicalUrl || '',
});

const mapStateToInput = (form: BlogFormState): BlogPostInput => ({
  title: form.title.trim(),
  slug: form.slug.trim(),
  excerpt: form.excerpt.trim(),
  content: form.content,
  status: form.status,
  publishAt: form.publishAt ? new Date(form.publishAt) : null,
  tags: form.tags
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean),
  coverImageUrl: form.coverImageUrl.trim() || undefined,
  seoTitle: form.seoTitle.trim() || undefined,
  seoDescription: form.seoDescription.trim() || undefined,
  ogImageUrl: form.ogImageUrl.trim() || undefined,
  canonicalUrl: form.canonicalUrl.trim() || undefined,
});

export const AdminBlogPage: React.FC = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<BlogFormState>(emptyState);

  const isEditing = !!editingId;

  const loadPosts = async () => {
    try {
      setLoading(true);
      setError(null);
      const loaded = await blogService.getAdminPosts(200);
      setPosts(loaded);
    } catch (err) {
      console.error('Failed to load blog posts:', err);
      setError('Failed to load blog posts.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPosts();
  }, []);

  const statusSummary = useMemo(() => {
    const summary: Record<BlogPostStatus, number> = {
      draft: 0,
      scheduled: 0,
      published: 0,
      archived: 0,
    };

    posts.forEach((post) => {
      summary[post.status] += 1;
    });

    return summary;
  }, [posts]);

  const onChange = (field: keyof BlogFormState, value: string) => {
    if (field === 'title') {
      setForm((prev) => ({
        ...prev,
        title: value,
        slug: prev.slug ? prev.slug : slugify(value),
      }));
      return;
    }

    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyState);
  };

  const handleEdit = (post: BlogPost) => {
    setEditingId(post.id);
    setForm(mapPostToState(post));
    setSuccess(null);
    setError(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const validate = (input: BlogPostInput) => {
    if (!input.title || !input.slug || !input.excerpt || !input.content) {
      return 'Title, slug, excerpt, and content are required.';
    }

    if (input.status === 'scheduled' && !input.publishAt) {
      return 'Scheduled posts must have a publish date/time.';
    }

    if ((input.status === 'scheduled' || input.status === 'published') && !input.publishAt) {
      return 'Published posts should have a publish date/time for ordering.';
    }

    return null;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!user) {
      setError('You must be authenticated.');
      return;
    }

    const input = mapStateToInput(form);
    const validationError = validate(input);

    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const slugTaken = await blogService.slugExists(input.slug, editingId || undefined);
      if (slugTaken) {
        setError('This slug is already in use. Please choose another one.');
        return;
      }

      if (editingId) {
        await blogService.updatePost(editingId, input);
        setSuccess('Blog post updated successfully.');
      } else {
        await blogService.createPost(input, user.uid, user.displayName || user.email || 'Admin');
        setSuccess('Blog post created successfully.');
      }

      await loadPosts();
      resetForm();
    } catch (err) {
      console.error('Failed to save blog post:', err);
      setError('Failed to save the blog post.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl font-bold text-neutral-900">Blog Management</h1>
        <p className="mt-1 text-neutral-600">Create, schedule, publish, and manage public blog posts.</p>
      </header>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-lg border p-3"><p className="text-xs text-neutral-500">Draft</p><p className="text-2xl font-semibold">{statusSummary.draft}</p></div>
        <div className="rounded-lg border p-3"><p className="text-xs text-neutral-500">Scheduled</p><p className="text-2xl font-semibold">{statusSummary.scheduled}</p></div>
        <div className="rounded-lg border p-3"><p className="text-xs text-neutral-500">Published</p><p className="text-2xl font-semibold">{statusSummary.published}</p></div>
        <div className="rounded-lg border p-3"><p className="text-xs text-neutral-500">Archived</p><p className="text-2xl font-semibold">{statusSummary.archived}</p></div>
      </section>

      <section className="rounded-xl border border-neutral-200 bg-white p-5">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold">{isEditing ? 'Edit Post' : 'New Post'}</h2>
          {isEditing && (
            <button type="button" onClick={resetForm} className="text-sm text-primary hover:underline">
              Cancel edit
            </button>
          )}
        </div>

        {error && <div className="mb-3 rounded-md bg-red-50 border border-red-200 p-3 text-red-700">{error}</div>}
        {success && <div className="mb-3 rounded-md bg-green-50 border border-green-200 p-3 text-green-700">{success}</div>}

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="text-sm">
              <span className="mb-1 block text-neutral-700">Title</span>
              <input className="w-full rounded-md border px-3 py-2" value={form.title} onChange={(e) => onChange('title', e.target.value)} />
            </label>
            <label className="text-sm">
              <span className="mb-1 block text-neutral-700">Slug</span>
              <input className="w-full rounded-md border px-3 py-2" value={form.slug} onChange={(e) => onChange('slug', slugify(e.target.value))} />
            </label>
          </div>

          <label className="text-sm block">
            <span className="mb-1 block text-neutral-700">Excerpt</span>
            <textarea className="w-full rounded-md border px-3 py-2 min-h-[80px]" value={form.excerpt} onChange={(e) => onChange('excerpt', e.target.value)} />
          </label>

          <div className="text-sm">
            <span className="mb-1 block text-neutral-700">Content</span>
            <RichTextEditor
              value={form.content}
              onChange={(value) => onChange('content', value)}
              placeholder="Write the blog post here. Use the toolbar for formatting, links, and images."
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <label className="text-sm">
              <span className="mb-1 block text-neutral-700">Status</span>
              <select className="w-full rounded-md border px-3 py-2" value={form.status} onChange={(e) => onChange('status', e.target.value)}>
                <option value="draft">Draft</option>
                <option value="scheduled">Scheduled</option>
                <option value="published">Published</option>
                <option value="archived">Archived</option>
              </select>
            </label>
            <label className="text-sm md:col-span-2">
              <span className="mb-1 block text-neutral-700">Publish At</span>
              <input className="w-full rounded-md border px-3 py-2" type="datetime-local" value={form.publishAt} onChange={(e) => onChange('publishAt', e.target.value)} />
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="text-sm">
              <span className="mb-1 block text-neutral-700">Tags (comma separated)</span>
              <input className="w-full rounded-md border px-3 py-2" value={form.tags} onChange={(e) => onChange('tags', e.target.value)} />
            </label>
            <label className="text-sm">
              <span className="mb-1 block text-neutral-700">Cover Image URL</span>
              <input className="w-full rounded-md border px-3 py-2" value={form.coverImageUrl} onChange={(e) => onChange('coverImageUrl', e.target.value)} />
            </label>
          </div>

          <details className="rounded-md border border-neutral-200 p-3">
            <summary className="cursor-pointer text-sm font-medium text-neutral-800">SEO and social metadata</summary>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="text-sm md:col-span-2">
                <span className="mb-1 block text-neutral-700">SEO Title</span>
                <input className="w-full rounded-md border px-3 py-2" value={form.seoTitle} onChange={(e) => onChange('seoTitle', e.target.value)} />
              </label>
              <label className="text-sm md:col-span-2">
                <span className="mb-1 block text-neutral-700">SEO Description</span>
                <textarea className="w-full rounded-md border px-3 py-2 min-h-[80px]" value={form.seoDescription} onChange={(e) => onChange('seoDescription', e.target.value)} />
              </label>
              <label className="text-sm">
                <span className="mb-1 block text-neutral-700">OG Image URL</span>
                <input className="w-full rounded-md border px-3 py-2" value={form.ogImageUrl} onChange={(e) => onChange('ogImageUrl', e.target.value)} />
              </label>
              <label className="text-sm">
                <span className="mb-1 block text-neutral-700">Canonical URL</span>
                <input className="w-full rounded-md border px-3 py-2" value={form.canonicalUrl} onChange={(e) => onChange('canonicalUrl', e.target.value)} />
              </label>
            </div>
          </details>

          <button type="submit" disabled={saving} className="rounded-md bg-primary px-4 py-2 text-white disabled:opacity-60">
            {saving ? 'Saving...' : isEditing ? 'Update post' : 'Create post'}
          </button>
        </form>
      </section>

      <section className="rounded-xl border border-neutral-200 bg-white p-5">
        <h2 className="text-xl font-semibold mb-4">All Posts</h2>

        {loading ? (
          <p className="text-neutral-600">Loading posts...</p>
        ) : posts.length === 0 ? (
          <p className="text-neutral-600">No blog posts created yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b text-left text-neutral-600">
                  <th className="py-2 pr-4">Title</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4">Publish At</th>
                  <th className="py-2 pr-4">Updated</th>
                  <th className="py-2 pr-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {posts.map((post) => (
                  <tr key={post.id} className="border-b align-top">
                    <td className="py-2 pr-4">
                      <p className="font-medium text-neutral-900">{post.title}</p>
                      <p className="text-neutral-500">/{post.slug}</p>
                    </td>
                    <td className="py-2 pr-4">
                      <span className="inline-flex rounded-full bg-neutral-100 px-2 py-0.5 text-xs text-neutral-700">{post.status}</span>
                    </td>
                    <td className="py-2 pr-4">{formatDateTime(post.publishAt || post.publishedAt)}</td>
                    <td className="py-2 pr-4">{formatDateTime(post.updatedAt)}</td>
                    <td className="py-2 pr-4">
                      <button type="button" className="text-primary hover:underline" onClick={() => handleEdit(post)}>
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};
